import React, { FC, useCallback } from 'react';
import { Field, FieldRenderProps, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { CancelToken } from 'axios';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';

import { FormTitle } from '~src/modules/okrs/manageObjectives/shared/labels/labelSelectorForm.sc';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { formatKeyresultProgressable } from '~src/modules/okrs/manageObjectives/shared/utils';
import { FieldType } from '~src/types';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { validatorRequired } from '~src/services/validators';
import SvgIcon from '~src/shared/SvgIcon';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import {
  DoubleNumbers,
  FormSectionWrapper,
  ButtonSection,
  SubInfo,
  BorderBox,
  FormInner,
  FormContainer,
} from '~src/modules/okrs/manageObjectives/create/addObjectiveEntityForm.sc';

import { KeyResultAdd } from './CreateKeyResults';
import { FormApi } from 'final-form';

interface FormValues {
  name: string;
  startValue: string;
  targetValue: string;
  owners: Segment[];
  weighting: number | null;
}

interface Props {
  header?: React.ReactNode;
  loadingAdd?: boolean;
  addKeyResult: (keyResult: KeyResultAdd) => void;
  selectedValues?: KeyResultAdd | null;
  hide: () => void;
  stayOnSubmit?: boolean;
}

const generateId = () => Math.random().toString(36).substring(2, 15).toUpperCase();

const AddKeyResultForm: FC<Props> = (props) => {
  const submitKeyResult = (data: FormValues, form: FormApi<FormValues>) => {
    const newKeyResult: KeyResultAdd = {
      ...data,
      startValue: (parseFloat(data.startValue) * 100).toFixed(2),
      targetValue: (parseFloat(data.targetValue) * 100).toFixed(2),
      id: !props.selectedValues ? generateId() : props.selectedValues.id,
    };

    props.addKeyResult(newKeyResult);
    form.restart();
  };

  const [t] = useTranslation();

  const renderInput = ({ autoFocus, input, label, meta: { touched, error } }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        autoFocus={autoFocus}
        label={label}
        labelKey={label}
        type={input.type}
        error={touched ? error : undefined}
      />
    );
  };

  const weightingValueMapper = (value: string) => (value === '' ? null : Math.min(parseInt(value, 10), 100));

  const typedSearch = (value: string, cancelToken: CancelToken) =>
    fetchSearchSegments(value, cancelToken, [SegmentType.User]);

  const renderChips = ({ input, meta: { touched, error } }: FieldRenderProps<any>) => {
    const onChangePeople = (param: Segment[]) => {
      return input.onChange(param);
    };

    return (
      <PeopleChips
        fetchSuggestions={typedSearch}
        chipsName="owners"
        selectedPeople={input.value ? input.value : []}
        onChangePeople={onChangePeople}
        errorTxt={touched ? error : null}
        t={t}
        placeHolderKey="x.questions.selection.chips.placeholder"
      />
    );
  };

  const handleOnCancel = useCallback(
    (form: FormApi<any>) => () => {
      form.restart();
      props.hide();
    },
    [props.hide],
  );

  return (
    <Form
      initialValues={
        props.selectedValues
          ? {
              ...props.selectedValues,
              startValue: formatKeyresultProgressable(parseFloat(props.selectedValues.startValue)),
              targetValue: formatKeyresultProgressable(parseFloat(props.selectedValues.targetValue)),
            }
          : {}
      }
      onSubmit={submitKeyResult}
      destroyOnUnregister
    >
      {({ handleSubmit, values, form }) => {
        const isInRangeValidator = (target: string) => {
          const targetNum = +target;
          if (isNaN(targetNum)) {
            return 'wrong-type';
          }
          if (targetNum === +values.startValue) {
            return 'not-equal';
          }
        };
        return (
          <FormContainer onSubmit={handleSubmit}>
            <FormInner>
              <FormTitle>
                {props.header || (
                  <span data-translation="x.okr.key.result.add.title">{t('x.okr.key.result.add.title')}</span>
                )}
              </FormTitle>
              <FormSectionWrapper>
                <SelectorHandler iconName="iconClipBoard">
                  {(_, select, blur) => (
                    <BorderBox>
                      <Field
                        autoFocus
                        name="name"
                        label={t('x.okr.key.result.input.placeholder')}
                        onFocus={select}
                        onBlur={blur}
                        children={renderInput}
                        validate={validatorRequired}
                      />
                    </BorderBox>
                  )}
                </SelectorHandler>
              </FormSectionWrapper>
              <FormSectionWrapper>
                <SelectorHandler iconName="iconFinishFlag">
                  {(_, select, blur) => (
                    <BorderBox>
                      <DoubleNumbers>
                        <Field
                          name="startValue"
                          label={t('x.okr.key.result.input.start.placeholder')}
                          onFocus={select}
                          onBlur={blur}
                          type="number"
                          children={renderInput}
                          validate={validatorRequired}
                        />
                        <Field
                          name="targetValue"
                          label={t('x.okr.key.result.input.target.placeholder')}
                          onFocus={select}
                          onBlur={blur}
                          type="number"
                          children={renderInput}
                          validate={composeValidators([validatorRequired, isInRangeValidator])}
                        />
                      </DoubleNumbers>
                    </BorderBox>
                  )}
                </SelectorHandler>
              </FormSectionWrapper>
              <FormSectionWrapper>
                <SelectorHandler iconName="iconPersonTool">
                  {() => (
                    <BorderBox $noBorder>
                      <Field name="owners" component={renderChips} validate={validatorRequired} />
                    </BorderBox>
                  )}
                </SelectorHandler>
              </FormSectionWrapper>
              <FormSectionWrapper>
                <SelectorHandler iconName="iconScale" viewBoxWidth={122.88} viewBoxHeight={105.9}>
                  {(_, select, blur) => (
                    <BorderBox>
                      <Field
                        name="weighting"
                        label={t('x.okr.add.weighting')}
                        onBlur={blur}
                        onFocus={select}
                        type="number"
                        children={renderInput}
                        parse={weightingValueMapper}
                      />
                    </BorderBox>
                  )}
                </SelectorHandler>
              </FormSectionWrapper>
              <SubInfo>
                <SvgIcon width={10} name="iconInfo" viewBoxWidth={15} />
                <span className="caption_text" data-translation="x.okr.key.result.add.sub-info">
                  {t('x.okr.key.result.add.sub-info')}
                </span>
              </SubInfo>
            </FormInner>
            <ButtonSection>
              <SecondaryButton
                type="button"
                onClick={handleOnCancel(form)}
                data-translation="x.okr.labels.button.cancel"
              >
                {t('x.okr.labels.button.cancel')}
              </SecondaryButton>
              <PrimaryButtonLoading
                isLoading={!!props.loadingAdd}
                data-translation="x.okr.labels.button.done"
              >
                {t('x.okr.labels.button.done')}
              </PrimaryButtonLoading>
            </ButtonSection>
          </FormContainer>
        );
      }}
    </Form>
  );
};

export default AddKeyResultForm;
